import React, { useState, useEffect } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import { InputText } from 'primereact/inputtext';
import { InputTextarea } from 'primereact/inputtextarea';
import { ProgressBar } from 'primereact/progressbar';
import { InputSwitch } from 'primereact/inputswitch';
import GenericFileUpload from '../../../../components/common/GenericFileUpload';
import LmsCodeDropdown from '../../components/LmsCodeDropdown';
import { useAddLmsVideoMutation, useUpdateLmsVideoMutation, useGenerateLmsVideoUploadUrlMutation, useCompleteLmsVideoUploadMutation } from '../../../../api';
import toastService from '../../../../utils/toastService';
import { useLanguage } from '../../../../contexts/LanguageContext';

// Bkz. docs/LmsVideoApi.md §2 "Kabul edilen dosyalar".
const MAX_FILE_SIZE = 2147483648; // 2 GB
const ACCEPTED_EXTENSIONS_LABEL = '.mp4, .avi, .mov, .wmv, .mkv, .flv, .webm';
const VIDEO_ACCEPTED_FILE_TYPES = {
    'video/mp4': ['.mp4'],
    'video/x-msvideo': ['.avi'],
    'video/quicktime': ['.mov'],
    'video/x-ms-wmv': ['.wmv'],
    'video/x-matroska': ['.mkv'],
    'video/x-flv': ['.flv'],
    'video/webm': ['.webm']
};

const formatFileSize = (bytes) => {
    if (!bytes) return '-';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

// S3'e doğrudan PUT — rootApi/axios KULLANILMAZ. Bu istemciler Authorization/X-App-Language
// başlıklarını otomatik eklediği için S3 ön imzalı adresin imzasını bozar ve istek
// `403 SignatureDoesNotMatch` ile reddedilir (bkz. docs/LmsVideoApi.md §2 "Kritik" notu).
// `fetch` ilerleme olayı vermediği için yüzde göstergesi için ham XMLHttpRequest kullanılıyor.
const uploadToS3WithProgress = ({ uploadUrl, file, contentType, onProgress }) =>
    new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        xhr.open('PUT', uploadUrl, true);
        xhr.setRequestHeader('Content-Type', contentType);
        xhr.upload.onprogress = (event) => {
            if (!event.lengthComputable) return;
            onProgress?.(Math.round((event.loaded * 100) / event.total));
        };
        xhr.onload = () => {
            if (xhr.status >= 200 && xhr.status < 300) {
                resolve();
                return;
            }
            reject(new Error(xhr.responseText || `S3 upload failed: ${xhr.status}`));
        };
        xhr.onerror = () => reject(new Error('S3 upload network error'));
        xhr.send(file);
    });

const emptyFormData = { modelCode: '', tittle: '', description: '', feeType: null, isPublic: false };

const LmsVideoFormDialog = ({ visible, onHide, editingVideo, onSuccess }) => {
    const { th } = useLanguage();

    const [formData, setFormData] = useState(emptyFormData);
    const [selectedFile, setSelectedFile] = useState(null);
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [uploadStep, setUploadStep] = useState(null); // 'creating' | 'saving' | 'generating' | 'uploading' | 'completing'

    const [addLmsVideo] = useAddLmsVideoMutation();
    const [updateLmsVideo] = useUpdateLmsVideoMutation();
    const [generateUploadUrl] = useGenerateLmsVideoUploadUrlMutation();
    const [completeUpload] = useCompleteLmsVideoUploadMutation();

    useEffect(() => {
        if (visible) {
            setFormData(
                editingVideo
                    ? {
                          modelCode: editingVideo.modelCode || '',
                          tittle: editingVideo.tittle || '',
                          description: editingVideo.description || '',
                          feeType: editingVideo.feeType ?? null,
                          isPublic: !!editingVideo.isPublic
                      }
                    : emptyFormData
            );
            setSelectedFile(null);
            setErrors({});
            setIsSubmitting(false);
            setUploadProgress(0);
            setUploadStep(null);
        }
    }, [visible, editingVideo]);

    const handleDialogClose = () => {
        if (isSubmitting) return;
        onHide();
    };

    const handleFilesChange = (files) => {
        setSelectedFile(files?.[0] || null);
        setErrors((prev) => ({ ...prev, file: null }));
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.modelCode?.trim()) newErrors.modelCode = th('lmsVideo.missingField');
        if (!formData.tittle?.trim()) newErrors.tittle = th('lmsVideo.missingField');
        if (formData.feeType === null || formData.feeType === undefined) newErrors.feeType = th('lmsVideo.missingField');
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const runUploadFlow = async (videoId, file) => {
        setUploadStep('generating');
        const generateResult = await generateUploadUrl({
            videoId,
            fileName: file.name,
            fileSize: file.size,
            expirationMinutes: 120
        }).unwrap();

        if (generateResult?.isSuccessful === false) {
            throw new Error(generateResult?.errorMessage || th('lmsVideo.uploadError'));
        }
        const { uploadUrl, fileKey, contentType } = generateResult?.data || {};
        if (!uploadUrl || !fileKey) {
            throw new Error(th('lmsVideo.uploadError'));
        }

        setUploadStep('uploading');
        setUploadProgress(0);
        await uploadToS3WithProgress({
            uploadUrl,
            file,
            contentType: contentType || file.type || 'video/mp4',
            onProgress: setUploadProgress
        });

        setUploadStep('completing');
        const completeResult = await completeUpload({ videoId, fileKey }).unwrap();
        if (completeResult?.isSuccessful === false) {
            throw new Error(completeResult?.errorMessage || th('lmsVideo.uploadError'));
        }
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const payload = {
                modelCode: formData.modelCode.trim(),
                tittle: formData.tittle.trim(),
                description: formData.description?.trim() || '',
                feeType: formData.feeType,
                isPublic: !!formData.isPublic
            };

            let videoId = editingVideo?.id;

            if (editingVideo) {
                setUploadStep('saving');
                const updateResult = await updateLmsVideo({ id: editingVideo.id, ...payload }).unwrap();
                if (updateResult?.isSuccessful === false) {
                    throw new Error(updateResult?.errorMessage || th('lmsVideo.saveError'));
                }
            } else {
                setUploadStep('creating');
                const addResult = await addLmsVideo(payload).unwrap();
                if (addResult?.isSuccessful === false) {
                    throw new Error(addResult?.errorMessage || th('lmsVideo.saveError'));
                }
                videoId = addResult?.data?.id;
                if (!videoId) {
                    throw new Error(th('lmsVideo.saveError'));
                }
            }

            if (selectedFile && videoId) {
                await runUploadFlow(videoId, selectedFile);
            }

            toastService.success(th('lmsVideo.saveSuccess'), th('common.success'));
            onSuccess?.();
            onHide();
        } catch (error) {
            const msg = error?.data?.message || error?.message || th('lmsVideo.saveError');
            toastService.error(msg, th('common.error'));
        } finally {
            setIsSubmitting(false);
            setUploadStep(null);
            setUploadProgress(0);
        }
    };

    const getStepLabel = () => {
        switch (uploadStep) {
            case 'creating':
                return th('lmsVideo.stepCreating');
            case 'saving':
                return th('lmsVideo.stepSaving');
            case 'generating':
                return th('lmsVideo.stepGenerating');
            case 'uploading':
                return `${th('lmsVideo.stepUploading')} %${uploadProgress}`;
            case 'completing':
                return th('lmsVideo.stepCompleting');
            default:
                return '';
        }
    };

    const footer = (
        <div className="flex justify-content-end gap-2">
            <Button label={th('common.cancel')} icon="pi pi-times" onClick={handleDialogClose} outlined disabled={isSubmitting} />
            <Button label={th('common.save')} icon="pi pi-check" onClick={handleSave} loading={isSubmitting} />
        </div>
    );

    return (
        <Dialog visible={visible} onHide={handleDialogClose} header={editingVideo ? th('lmsVideo.editTitle') : th('lmsVideo.newTitle')} modal className="p-fluid" footer={footer} style={{ width: '40rem', maxWidth: '95vw' }} closable={!isSubmitting}>
            <div className="field mb-3">
                <label htmlFor="lmsVideo-modelCode">
                    {th('lmsVideo.fieldModelCode')} <span className="p-error">*</span>
                </label>
                <InputText id="lmsVideo-modelCode" value={formData.modelCode} maxLength={50} onChange={(e) => setFormData((prev) => ({ ...prev, modelCode: e.target.value }))} disabled={isSubmitting} className={errors.modelCode ? 'p-invalid' : ''} />
                {errors.modelCode && <small className="p-error">{errors.modelCode}</small>}
            </div>

            <div className="field mb-3">
                <label htmlFor="lmsVideo-tittle">
                    {th('lmsVideo.fieldTitle')} <span className="p-error">*</span>
                </label>
                <InputText id="lmsVideo-tittle" value={formData.tittle} maxLength={500} onChange={(e) => setFormData((prev) => ({ ...prev, tittle: e.target.value }))} disabled={isSubmitting} className={errors.tittle ? 'p-invalid' : ''} />
                {errors.tittle && <small className="p-error">{errors.tittle}</small>}
            </div>

            <div className="field mb-3">
                <label htmlFor="lmsVideo-description">{th('lmsVideo.fieldDescription')}</label>
                <InputTextarea id="lmsVideo-description" value={formData.description} maxLength={500} rows={3} onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))} disabled={isSubmitting} />
            </div>

            <div className="field mb-3">
                <label htmlFor="lmsVideo-feeType">
                    {th('lmsVideo.fieldFeeType')} <span className="p-error">*</span>
                </label>
                <LmsCodeDropdown
                    id="lmsVideo-feeType"
                    groupCode="VIDEO_FEE_TYPE"
                    value={formData.feeType}
                    onChange={(value) => setFormData((prev) => ({ ...prev, feeType: value }))}
                    placeholder={th('common.select')}
                    disabled={isSubmitting}
                    invalid={!!errors.feeType}
                />
                {errors.feeType && <small className="p-error">{errors.feeType}</small>}
            </div>

            <div className="field mb-3">
                <label htmlFor="lmsVideo-isPublic">{th('lmsVideo.fieldVisibility')}</label>
                <div className="flex align-items-center gap-2">
                    <InputSwitch inputId="lmsVideo-isPublic" checked={!!formData.isPublic} onChange={(e) => setFormData((prev) => ({ ...prev, isPublic: e.value }))} disabled={isSubmitting} />
                    <span>{th(formData.isPublic ? 'lmsVideo.visibilityPublic' : 'lmsVideo.visibilityPrivate')}</span>
                </div>
            </div>

            {editingVideo?.fileKey && (
                <div className="field mb-3">
                    <label>{th('lmsVideo.currentFileInfo')}</label>
                    <div className="flex flex-column gap-1 p-3 border-round surface-100">
                        <div className="flex gap-2 align-items-center">
                            <i className="pi pi-video text-primary" />
                            <span className="font-semibold text-sm">{editingVideo.fileName?.split('/').pop() || editingVideo.fileName}</span>
                        </div>
                        {editingVideo.fileSize && <span className="text-color-secondary text-sm ml-4">{formatFileSize(editingVideo.fileSize)}</span>}
                    </div>
                    <small className="text-color-secondary">{th('lmsVideo.replaceFileHint')}</small>
                </div>
            )}

            <div className="field mb-3">
                <label htmlFor="lmsVideo-file">{th('lmsVideo.fieldFile')}</label>
                <GenericFileUpload
                    newFiles={!isSubmitting ? (selectedFile ? [selectedFile] : []) : []}
                    onFilesChange={!isSubmitting ? handleFilesChange : () => {}}
                    existingDocuments={[]}
                    isLoading={isSubmitting}
                    disabled={isSubmitting}
                    multiple={false}
                    maxFileSize={MAX_FILE_SIZE}
                    acceptedFileTypes={VIDEO_ACCEPTED_FILE_TYPES}
                    showDownloadButton={false}
                    showUpdateButton={false}
                    showDeleteButton
                />
                <small className="text-color-secondary">
                    {th('lmsVideo.acceptedFormatsLabel')}: {ACCEPTED_EXTENSIONS_LABEL} — {formatFileSize(MAX_FILE_SIZE)}
                </small>
                {!editingVideo && <small className="block text-color-secondary mt-1">{th('lmsVideo.fileOptionalHint')}</small>}
                {errors.file && <small className="p-error block">{errors.file}</small>}
            </div>

            {isSubmitting && uploadStep && (
                <div className="mt-3">
                    <div className="flex align-items-center gap-2 mb-2">
                        <i className="pi pi-spin pi-spinner text-primary" />
                        <span className="text-sm font-semibold">{getStepLabel()}</span>
                    </div>
                    {uploadStep === 'uploading' && <ProgressBar value={uploadProgress} showValue displayValueTemplate={(v) => `${v}%`} style={{ height: '1.2rem' }} />}
                </div>
            )}
        </Dialog>
    );
};

export default LmsVideoFormDialog;
